import { useState } from 'react';
import { MUSCLE_GROUPS } from '../../data/constants';
import { StatCards, SectionCard, HBar, MiniLineChart, EmptyState, parseDateStr, weekLabel, toDateKey } from './shared';

const estimateOneRm = (weight, reps) => {
    const w = +weight || 0, r = +reps || 1;
    if (!w) return 0;
    return r <= 1 ? w : Math.round(w * (1 + Math.min(r, 30) / 30));
};

function WeightBodyChart({ weights }) {
    if (weights.length < 2) {
        if (!weights.length) return <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '16px 0' }}>체중 기록이 없어요</div>;
        return <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '16px 0' }}>{weights[0].val}kg · 2개 이상 기록하면 그래프가 표시돼요</div>;
    }
    const data = [...weights].sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-20);
    const vals = data.map(w => +w.val);
    const minV = Math.min(...vals) - 0.5, maxV = Math.max(...vals) + 0.5;
    const range = maxV - minV || 1;
    const W = 340, H = 90, padX = 30, padY = 16;
    const px = i => padX + (i / (data.length - 1)) * (W - padX * 2);
    const py = v => H - padY - ((v - minV) / range) * (H - padY * 2);
    const points = data.map((w, i) => `${px(i)},${py(+w.val)}`).join(' ');
    const latest = data[data.length - 1], first = data[0];
    const diff = (+latest.val - +first.val).toFixed(1);
    const diffColor = +diff < 0 ? 'var(--accent)' : +diff > 0 ? 'var(--coral)' : 'var(--text3)';
    return (
        <div className="chart-wrap">
            <div className="chart-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>체중 추이 (최근 {data.length}회)</span>
                <span style={{ fontSize: 11, fontFamily: 'DM Mono,monospace', color: diffColor }}>{+diff > 0 ? '+' : ''}{diff}kg</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{ height: 100, width: '100%', overflow: 'visible' }}>
                <defs>
                    <linearGradient id="wg2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#c8f764" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#c8f764" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {[0, 0.5, 1].map(t => {
                    const yVal = minV + t * range;
                    return (
                        <g key={t}>
                            <line x1={padX} y1={py(yVal)} x2={W - padX} y2={py(yVal)} stroke="var(--bg3)" strokeWidth="1" />
                            <text x={padX - 4} y={py(yVal) + 4} fontSize="8" fill="var(--text3)" textAnchor="end" fontFamily="DM Mono,monospace">{yVal.toFixed(1)}</text>
                        </g>
                    );
                })}
                <polygon points={`${px(0)},${H - padY} ${points} ${px(data.length - 1)},${H - padY}`} fill="url(#wg2)" />
                <polyline points={points} fill="none" stroke="#c8f764" strokeWidth="1.8" strokeLinejoin="round" />
                {data.map((w, i) => <circle key={i} cx={px(i)} cy={py(+w.val)} r="2.5" fill="#c8f764" />)}
                {[0, data.length - 1].map(i => (
                    <text key={i} x={px(i)} y={py(+data[i].val) - 7} fontSize="10" fill="var(--text2)" textAnchor={i === 0 ? 'start' : 'end'} fontFamily="DM Mono,monospace">{data[i].val}kg</text>
                ))}
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', fontFamily: 'DM Mono,monospace' }}>
                <span>{first.date}</span><span>{latest.date}</span>
            </div>
        </div>
    );
}

export default function WeightAnalysis({ records, weights }) {
    const [prUnit, setPrUnit] = useState('day');
    const [prMuscle, setPrMuscle] = useState('all');

    if (!records.length) return <EmptyState text="웨이트 트레이닝 기록이 없어요." />;

    const totalMin = records.reduce((a, r) => a + (+r.duration || 0), 0);

    // 총 볼륨 (weight × reps × sets)
    let totalVol = 0;
    records.forEach(r => {
        (r.exercises || []).forEach(ex => {
            (ex.sets || []).forEach(s => { totalVol += (+s.weight || 0) * (+s.reps || 0); });
        });
    });

    // 근육군별 세션 수
    const muscleMap = {};
    records.forEach(r => {
        const groups = new Set();
        (r.exercises || []).forEach(ex => { if (ex.muscleGroup) groups.add(ex.muscleGroup); });
        (r.muscleGroups || []).forEach(m => groups.add(m));
        groups.forEach(m => { muscleMap[m] = (muscleMap[m] || 0) + 1; });
    });
    const topMuscles = Object.entries(muscleMap).sort((a, b) => b[1] - a[1]).slice(0, 8);
    const maxMuscle = topMuscles[0]?.[1] || 1;

    // PR 목록 (운동별 최고 무게)
    const prMap = {};
    records.forEach(r => {
        (r.exercises || []).forEach(ex => {
            if (!ex.name) return;
            (ex.sets || []).forEach(s => {
                const w = +s.weight || 0;
                if (w > (prMap[ex.name] || 0)) prMap[ex.name] = w;
            });
        });
    });
    const prList = Object.entries(prMap).sort((a, b) => b[1] - a[1]).slice(0, 10);

    // PR 1RM 추이 차트
    const bucketLabel = d => {
        const dt = parseDateStr(d);
        if (prUnit === 'month') return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
        if (prUnit === 'week') { const s = new Date(dt); s.setDate(dt.getDate() - dt.getDay()); return toDateKey(s); }
        return d;
    };
    const displayLabel = key => {
        const d = parseDateStr(key);
        if (prUnit === 'month') return `${d.getMonth() + 1}월`;
        if (prUnit === 'week') return weekLabel(d);
        return `${d.getMonth() + 1}/${d.getDate()}`;
    };
    const bucketMap = {};
    records.forEach(r => {
        (r.exercises || []).forEach(ex => {
            if (prMuscle !== 'all' && ex.muscleGroup !== prMuscle) return;
            (ex.sets || []).forEach(s => {
                const orm = estimateOneRm(s.weight, s.reps);
                if (!orm) return;
                const key = bucketLabel(r.date);
                bucketMap[key] = Math.max(bucketMap[key] || 0, orm);
            });
        });
    });
    let best = 0;
    const prTrendData = Object.entries(bucketMap)
        .sort(([a], [b]) => parseDateStr(a) - parseDateStr(b))
        .map(([key, val]) => { best = Math.max(best, val); return { label: displayLabel(key), val: best }; })
        .slice(-12);

    const muscleColor = (m) => {
        const colors = { '가슴': '#5ba4f5', '등': '#9b8cff', '어깨': '#ffa44f', '하체': '#ff7c5c', '이두': '#c8f764', '삼두': '#6be0c8', '복근': '#ff5c8a' };
        return colors[m] || 'var(--accent)';
    };

    return (
        <>
            <StatCards items={[
                { val: `${records.length}회`, label: '총 세션' },
                { val: `${Math.round(totalMin / 60)}h`, label: '총 시간' },
                { val: `${Math.round(totalVol / 1000)}t`, label: '총 볼륨' },
                { val: `${prList.length}개`, label: 'PR 종목' },
            ]} />

            {topMuscles.length > 0 && (
                <SectionCard title="근육군 분포" desc="어떤 부위를 많이 훈련했는지">
                    {topMuscles.map(([m, cnt]) => (
                        <HBar key={m} label={m} value={cnt} max={maxMuscle} color={muscleColor(m)} suffix="세션" />
                    ))}
                </SectionCard>
            )}

            {prTrendData.length >= 2 && (
                <SectionCard title="추정 1RM 추이" desc="최고 추정 1RM의 성장 흐름">
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        {[['day', '일'], ['week', '주'], ['month', '월']].map(([k, l]) => (
                            <button key={k} className={`toggle-btn${prUnit === k ? ' active' : ''}`} onClick={() => setPrUnit(k)}>{l}</button>
                        ))}
                        <select className="form-input pr-muscle-select" value={prMuscle} onChange={e => setPrMuscle(e.target.value)} style={{ marginLeft: 4 }}>
                            <option value="all">전체</option>
                            {MUSCLE_GROUPS.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <MiniLineChart data={prTrendData} color="#5ba4f5" />
                </SectionCard>
            )}

            {prList.length > 0 && (
                <SectionCard title="운동별 최고 무게" desc="지금까지 든 최고 무게 기록">
                    <div className="pr-list">
                        {prList.map(([name, w]) => (
                            <div key={name} className="pr-item">
                                <span className="pr-name">{name}</span>
                                <span className="pr-val">{w}kg</span>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            )}

            {weights?.length > 0 && (
                <SectionCard title="체중 변화" desc="체중 기록 추이">
                    <WeightBodyChart weights={weights} />
                </SectionCard>
            )}
        </>
    );
}
